import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { employeeFormSchema, EmployeeFormValues } from '@/schemas/employee';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarIcon, Eye, EyeOff, BadgePlus, SquarePen } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { useRoleContext } from '@/hooks/useRoleContext';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { EmployeeFormData } from '@/api/employee';
import { useCreateEmployee, useUpdateEmployee } from '@/hooks/useEmployee';
import { EmployeeDTO } from '@/hooks/useEmployee';
import LoadingCircle from '@/components/LoadingCircle';

function CreateUpdate({ employee }: { employee?: EmployeeDTO }) {
  const { roles } = useRoleContext();
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const { mutateAsync: createEmployee, isPending } = useCreateEmployee();
  const { mutateAsync: updateEmployee, isPending: isUpdating } =
    useUpdateEmployee();

  const isEdit = !!employee;
  const form = useForm<EmployeeFormValues>({
    resolver: zodResolver(employeeFormSchema(isEdit)),
    defaultValues: {
      name: employee?.username || '',
      emailCreate: employee?.email || '',
      passwordCreate: '',
      role: parseInt(employee?.role.id || '0'),
      dateOfBirth: employee?.dateOfBirth
        ? new Date(employee.dateOfBirth)
        : undefined,
      phoneNumber: employee?.phone || '',
      address: employee?.address || '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (employee) {
      form.reset({
        name: employee.username || '',
        emailCreate: employee.email || '',
        passwordCreate: '',
        role: parseInt(employee.role.id || '0'),
        dateOfBirth: employee.dateOfBirth
          ? new Date(employee.dateOfBirth)
          : new Date(),
        phoneNumber: employee.phone || '',
        address: employee.address || '',
      });
    }
  }, [employee, form]);

  const createEmployeePayload = (
    values: EmployeeFormValues
  ): EmployeeFormData => ({
    name: values.name,
    email: values.emailCreate,
    password: values.passwordCreate,
    roleId: values.role,
    dateOfBirth: format(values.dateOfBirth, 'yyyy-MM-dd'),
    phoneNumber: values.phoneNumber,
    address: values.address,
  });

  const updateEmployeePayload = (
    values: EmployeeFormValues
  ): EmployeeFormData => ({
    name: values.name,
    email: values.emailCreate,
    roleId: values.role,
    dateOfBirth: format(values.dateOfBirth, 'yyyy-MM-dd'),
    phoneNumber: values.phoneNumber,
    address: values.address,
  });

  async function onSubmit(values: EmployeeFormValues) {
    if (isEdit) {
      await updateEmployee({
        id: employee.id,
        data: updateEmployeePayload(values),
      });
    } else {
      await createEmployee(createEmployeePayload(values));
    }
    setOpen(false);
    form.reset();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        form.reset();
        setOpen(value);
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant={isEdit ? 'outline' : 'default'}
          className={isEdit ? 'luxury-button-outline' : 'luxury-button-primary'}
        >
          {isEdit ? (
            <>
              <SquarePen className="w-4 h-4" />
              Sửa
            </>
          ) : (
            <>
              <BadgePlus className="w-4 h-4" />
              Thêm mới
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="min-w-[800px] max-h-[95vh] overflow-y-auto luxury-dialog"
        aria-describedby="dialog-description"
      >
        <DialogDescription className="sr-only">
          {isEdit ? 'Sửa nhân viên' : 'Thêm nhân viên'}
        </DialogDescription>
        <DialogHeader className="luxury-header">
          <DialogTitle className="luxury-title">
            {isEdit ? 'Cập nhật thông tin nhân viên' : 'Thêm nhân viên mới'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-8"
            autoComplete="off"
          >
            <div className="luxury-card">
              <h3 className="luxury-subtitle">Thông tin cơ bản</h3>
              <div className="flex flex-col gap-6 items-start">
                <div className="flex gap-6 w-full items-start">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Tên nhân viên
                        </Label>
                        <FormControl>
                          <Input {...field} className="luxury-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="emailCreate"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Email
                        </Label>
                        <FormControl>
                          <Input
                            {...field}
                            className="luxury-input"
                            autoComplete="off"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {!isEdit && (
                  <FormField
                    control={form.control}
                    name="passwordCreate"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label required className="luxury-label">
                          Mật khẩu
                        </Label>
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              type={showPassword ? 'text' : 'password'}
                              className="luxury-input pr-10"
                              autoComplete="off"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent luxury-icon-highlight"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <div className="flex gap-6 w-full items-start">
                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Vai trò
                        </Label>
                        <FormControl>
                          <Select
                            {...field}
                            value={field.value.toString()}
                            onValueChange={(value) =>
                              field.onChange(Number(value))
                            }
                          >
                            <SelectTrigger className="w-full luxury-input">
                              <SelectValue placeholder="Chọn vai trò" />
                            </SelectTrigger>
                            <SelectContent className="border-luxury-200">
                              {roles?.map((role) => (
                                <SelectItem
                                  key={role.id}
                                  value={role.id.toString()}
                                >
                                  {role.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="dateOfBirth"
                    render={({ field }) => (
                      <FormItem className="flex flex-col w-1/2">
                        <Label required className="luxury-label">
                          Ngày sinh
                        </Label>
                        <Popover
                          open={datePickerOpen}
                          onOpenChange={setDatePickerOpen}
                        >
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={'outline'}
                                className={cn(
                                  'w-full pl-3 text-left font-normal border-luxury-200 hover:bg-luxury-50 hover:text-luxury-900 bg-white',
                                  !field.value && 'text-muted-foreground'
                                )}
                              >
                                {field.value ? (
                                  format(field.value, 'dd/MM/yyyy')
                                ) : (
                                  <span>Chọn ngày sinh</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto p-0 border-luxury-200"
                            align="start"
                          >
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={(date) => {
                                field.onChange(date);
                                setDatePickerOpen(false);
                              }}
                              disabled={(date: Date) =>
                                date > new Date() ||
                                date < new Date('1900-01-01')
                              }
                              initialFocus
                              className="rounded-md border-luxury-200"
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="luxury-card">
              <h3 className="luxury-subtitle">Thông tin liên hệ</h3>
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <Label required className="luxury-label">
                        Số điện thoại
                      </Label>
                      <FormControl>
                        <Input {...field} className="luxury-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <Label required className="luxury-label">
                        Địa chỉ
                      </Label>
                      <FormControl>
                        <Input {...field} className="luxury-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full mt-6 luxury-button-primary"
              disabled={isPending || isUpdating}
            >
              {isPending || isUpdating ? (
                <LoadingCircle />
              ) : (
                <>{isEdit ? 'Cập nhật thông tin' : 'Tạo mới nhân viên'}</>
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default CreateUpdate;
